import React, { useState, useEffect, useMemo } from 'react';
import { Stack, Title, Text, Group, Image, useMantineTheme, useComputedColorScheme } from '@mantine/core';
import { MenuItem as MenuItemType, IngredientAnalysis } from '../../types/menu';
import { IngredientHighlights } from './IngredientHighlights';
import { MenuItemQuestion } from './MenuItemQuestion';

interface MenuItemDetailsProps {
  item: MenuItemType;
  getDisplayText: (item: MenuItemType, field: 'name' | 'description' | 'disclaimer') => string;
  menuId?: string;
  restaurantName?: string;
  restaurantDescription?: string;
  currentLanguage?: string;
  /** Answers questions instead of the menu's own endpoint (e.g. the scan page, which has no menu). */
  askQuestion?: (question: string) => Promise<string>;
  /** Extra sections (the scan page's guide notes), shown after the ingredient highlights and before the questions. */
  children?: React.ReactNode;
}

/** Capitalize each word for display */
function toTitleCase(s: string): string {
  if (!s?.trim()) return s;
  return s.trim().replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

/** Get the display name for an ingredient based on current language */
function getIngredientDisplayName(ingredient: IngredientAnalysis, currentLanguage: string): string {
  const raw = currentLanguage === 'jp'
    ? (ingredient.name_jp || ingredient.name || ingredient.canonical_name || '')
    : (ingredient.name_en || ingredient.name || ingredient.canonical_name || '');
  return toTitleCase(raw);
}

/**
 * The SmartMenu v2 view of one menu item: image, name, price, ingredients and
 * description, disclaimer, ingredient highlights and questions (FAQ).
 * Shown in MenuItemModal on a menu, and in the sheet on the scan page.
 */
export const MenuItemDetails: React.FC<MenuItemDetailsProps> = ({
  item,
  getDisplayText,
  menuId,
  restaurantName,
  restaurantDescription,
  currentLanguage = 'en',
  askQuestion,
  children
}) => {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const isDarkMode = colorScheme === 'dark';

  // Ingredients come exclusively from the centralized Ingredient model (item.ingredients),
  // never from the stale props.analysis.ingredients snapshot.
  const linkedIngredients: IngredientAnalysis[] = item?.ingredients || [];

  const RARITY_RANK: Record<string, number> = { very_rare: 3, rare: 2, uncommon: 1, common: 0 };

  // Comma-separated list: only uncommon / rare (not common) and where effective display is true
  const uncommonIngredients = useMemo(
    () => linkedIngredients.filter(i => {
      if ((RARITY_RANK[i.rarity] ?? 0) < 1) return false;
      const override = (i as any).MenuItemIngredient?.display_override;
      if (override === true) return true;
      if (override === false) return false;
      return i.display === true;
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item?.id, linkedIngredients.length]
  );

  // Highlights section: ingredients where effective display is true.
  // display_override on the junction takes precedence over the global ingredient.display flag.
  const displayIngredients = useMemo(
    () => linkedIngredients.filter(i => {
      const override = (i as any).MenuItemIngredient?.display_override;
      if (override === true) return true;
      if (override === false) return false;
      return i.display === true;
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item?.id, linkedIngredients.length]
  );

  // Auto-show highlights when the comma list has anything to show
  const hasNonCommonIngredient = uncommonIngredients.length > 0;

  // Default focus: rarest ingredient among those shown in highlights
  const rarestDisplayIngredient = useMemo(() => {
    if (displayIngredients.length === 0) return null;
    return [...displayIngredients].sort(
      (a, b) => (RARITY_RANK[b.rarity] ?? 0) - (RARITY_RANK[a.rarity] ?? 0)
    )[0];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id, displayIngredients.length]);

  const [highlightsVisible, setHighlightsVisible] = useState(false);
  const [focusedIngredient, setFocusedIngredient] = useState<string | null>(null);

  // On open: show highlights if non-common ingredients exist; focus the rarest displayable one
  useEffect(() => {
    setHighlightsVisible(hasNonCommonIngredient);
    setFocusedIngredient(rarestDisplayIngredient?.canonical_name ?? null);
  }, [item?.id, hasNonCommonIngredient, rarestDisplayIngredient]);

  const itemName = getDisplayText(item, 'name');
  const itemDescription = getDisplayText(item, 'description');
  const itemDisclaimer = getDisplayText(item, 'disclaimer');
  const priceYen = Math.floor(Number(item.price));
  const showPrice = priceYen > 0;

  const handleIngredientClick = (ingredient: IngredientAnalysis) => {
    setHighlightsVisible(true);
    setFocusedIngredient(ingredient.canonical_name);
  };

  return (
    <Stack gap={0}>
      {item.imageUrl && (
        <Image
          src={item.imageUrl}
          alt={`${itemName} image`}
          height="min(18vh, 128px)"
          fit="cover"
          fallbackSrc="https://via.placeholder.com/800x450/e9ecef/6c757d?text=No+Image"
          style={{ aspectRatio: '16 / 9' }}
        />
      )}

      <Stack
        gap="sm"
        style={{
          padding: theme.spacing.lg,
          paddingBottom: theme.spacing.lg
        }}
      >
        <Title order={3}>{itemName}</Title>

        {showPrice && (
          <Group justify="space-between" align="baseline">
            <Text fw={600} size="lg">{priceYen} 円</Text>
            {item.name && item.name !== itemName && (
              <Text size="sm" c="dimmed" style={{ fontStyle: 'italic' }}>{item.name}</Text>
            )}
          </Group>
        )}
        {!showPrice && item.name && item.name !== itemName && (
          <Text size="sm" c="dimmed" style={{ fontStyle: 'italic' }}>{item.name}</Text>
        )}

        {/* Inline description: "Rare Ingredient, Uncommon Ingredient — description" or just description */}
        {(uncommonIngredients.length > 0 || itemDescription) && (
          <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
            {uncommonIngredients.map((ingredient, index) => {
              const displayName = getIngredientDisplayName(ingredient, currentLanguage);
              return (
                <React.Fragment key={ingredient.canonical_name || index}>
                  <Text
                    component="span"
                    size="sm"
                    onClick={() => handleIngredientClick(ingredient)}
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      textDecorationStyle: ingredient.canonical_name === focusedIngredient ? 'solid' : 'dotted',
                      textUnderlineOffset: '2px',
                      color: ingredient.canonical_name === focusedIngredient
                        ? theme.colors.orange[6]
                        : (isDarkMode ? theme.colors.gray[3] : theme.colors.dark[5]),
                      fontWeight: ingredient.canonical_name === focusedIngredient ? 600 : undefined,
                    }}
                  >
                    {displayName}
                  </Text>
                  {index < uncommonIngredients.length - 1 && (
                    <Text component="span" size="sm" c="dimmed">, </Text>
                  )}
                </React.Fragment>
              );
            })}
            {uncommonIngredients.length > 0 && itemDescription && (
              <Text component="span" size="sm" c="dimmed"> — </Text>
            )}
            {itemDescription && (
              <Text component="span" size="sm" c="dimmed">{itemDescription}</Text>
            )}
          </Text>
        )}

        {/* Disclaimer — practical ordering notes (wait times, portion options, order-ahead) */}
        {itemDisclaimer && (
          <Text size="xs" c="dimmed" style={{ lineHeight: 1.5 }}>
            {itemDisclaimer}
          </Text>
        )}

        {/* Ingredient highlights — auto-shown when non-common ingredients exist, rarest focused */}
        {highlightsVisible && displayIngredients.length > 0 && (
          <IngredientHighlights
            ingredients={displayIngredients}
            currentLanguage={currentLanguage}
            focusedIngredient={focusedIngredient}
          />
        )}

        {children}

        {/* Menu Item Question Component */}
        {(menuId || askQuestion) && (
          <MenuItemQuestion
            menuItem={item}
            menuId={menuId}
            restaurantName={restaurantName}
            restaurantDescription={restaurantDescription}
            currentLanguage={currentLanguage}
            faq={item.props?.menuFaq}
            displayName={itemName}
            askQuestion={askQuestion}
          />
        )}
      </Stack>
    </Stack>
  );
};
